import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Pencil, Trash2, ExternalLink, Search, FileText, ArrowLeft } from "lucide-react";

export default function AdminBlogPage() {
  const { user, loading: authLoading } = useAuth({ redirectIfUnauthed: true });
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editingSlug, setEditingSlug] = useState(null);
  const [deleting, setDeleting] = useState(null); // {slug, title}

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/blog/posts?limit=500");
      setPosts(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load posts.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user?.is_admin) {
      refresh();
    }
  }, [authLoading, user, refresh]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0F0D0C] text-[#A39A8F] flex items-center justify-center font-mono text-xs uppercase tracking-[0.25em]">
        Loading…
      </div>
    );
  }

  if (!user) return null;  // useAuth will redirect

  if (!user.is_admin) {
    return (
      <div className="min-h-screen bg-[#0F0D0C] text-[#F5F2EB] grain" data-testid="admin-blog-forbidden">
        <Navbar user={user} />
        <div className="mx-auto max-w-3xl px-6 py-32 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-4">403</div>
          <h1 className="font-serif text-4xl mb-4">Admins only.</h1>
          <p className="font-mono text-sm text-[#A39A8F] mb-8">
            This area is reserved for AgentReply admins. If that's meant to be you, your founder needs to promote your account first.
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            data-testid="back-to-dashboard"
            className="font-mono text-xs uppercase tracking-[0.25em] border border-[#F5F2EB]/20 px-6 py-3 hover:border-[#D95B38] hover:text-[#D95B38]"
          >
            ← Back to dashboard
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const filtered = posts.filter((p) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (p.title || "").toLowerCase().includes(s) ||
           (p.slug || "").toLowerCase().includes(s) ||
           (p.tags || []).some((t) => t.toLowerCase().includes(s));
  });

  return (
    <div className="min-h-screen bg-[#0F0D0C] text-[#F5F2EB] grain" data-testid="admin-blog-page">
      <Helmet>
        <title>Blog admin · AgentReply</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <Navbar user={user} />

      <div className="mx-auto max-w-7xl px-6 md:px-12 py-12">
        <button
          onClick={() => navigate("/dashboard")}
          data-testid="admin-back"
          className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8F] hover:text-[#D95B38] mb-8 inline-flex items-center gap-2"
        >
          <ArrowLeft className="w-3 h-3" /> Back to dashboard
        </button>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-3 flex items-center gap-2">
              <FileText className="w-3 h-3" /> Admin / Blog posts
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-none">
              Edit & curate <span className="italic text-[#D95B38]">your blog</span>
            </h1>
            <p className="font-mono text-xs text-[#A39A8F] mt-3">
              {posts.length} post{posts.length === 1 ? "" : "s"} in the database · TrafficForge auto-ingestion stays live.
            </p>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A7068]" strokeWidth={1.5} />
            <Input
              data-testid="admin-blog-search"
              placeholder="Search title, slug, tag…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-[#1A1715] border-[#F5F2EB]/15 pl-10 w-72 font-mono text-xs"
            />
          </div>
        </div>

        {loading ? (
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#7A7068] py-24 text-center" data-testid="admin-blog-loading">
            Loading posts…
          </div>
        ) : filtered.length === 0 ? (
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#7A7068] py-24 text-center border border-dashed border-[#F5F2EB]/10" data-testid="admin-blog-empty">
            {posts.length === 0 ? "No posts yet. TrafficForge will populate this automatically." : "No posts match your search."}
          </div>
        ) : (
          <div className="border border-[#F5F2EB]/10 bg-[#1A1715]" data-testid="admin-blog-table">
            <Table>
              <TableHeader>
                <TableRow className="border-[#F5F2EB]/10 hover:bg-transparent">
                  <TableHead className="text-[#A39A8F] font-mono text-[10px] uppercase tracking-[0.25em]">Title</TableHead>
                  <TableHead className="text-[#A39A8F] font-mono text-[10px] uppercase tracking-[0.25em] hidden md:table-cell">Slug</TableHead>
                  <TableHead className="text-[#A39A8F] font-mono text-[10px] uppercase tracking-[0.25em] hidden lg:table-cell">Published</TableHead>
                  <TableHead className="text-[#A39A8F] font-mono text-[10px] uppercase tracking-[0.25em]">Status</TableHead>
                  <TableHead className="text-[#A39A8F] font-mono text-[10px] uppercase tracking-[0.25em] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p) => (
                  <TableRow key={p.slug} className="border-[#F5F2EB]/10 hover:bg-[#0F0D0C]" data-testid={`admin-post-row-${p.slug}`}>
                    <TableCell>
                      <div className="font-serif text-base text-[#F5F2EB]">{p.title}</div>
                      <div className="font-mono text-[10px] text-[#7A7068] mt-1 md:hidden">{p.slug}</div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell font-mono text-[11px] text-[#A39A8F]">{p.slug}</TableCell>
                    <TableCell className="hidden lg:table-cell font-mono text-[11px] text-[#A39A8F]">
                      {p.published_at ? new Date(p.published_at).toLocaleDateString() : "—"}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`font-mono text-[10px] uppercase tracking-[0.25em] px-2 py-1 border ${
                          p.status === "published"
                            ? "border-[#D95B38]/40 text-[#D95B38]"
                            : "border-[#F5F2EB]/15 text-[#A39A8F]"
                        }`}
                      >
                        {p.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <a
                          href={`/blog/${p.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`admin-post-view-${p.slug}`}
                          className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] hover:text-[#D95B38] inline-flex items-center gap-1 px-2 py-1"
                          aria-label={`View ${p.title}`}
                        >
                          <ExternalLink className="w-3 h-3" /> View
                        </a>
                        <button
                          onClick={() => setEditingSlug(p.slug)}
                          data-testid={`admin-post-edit-${p.slug}`}
                          className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#F5F2EB] hover:text-[#D95B38] inline-flex items-center gap-1 px-2 py-1 border border-[#F5F2EB]/15 hover:border-[#D95B38]"
                          aria-label={`Edit ${p.title}`}
                        >
                          <Pencil className="w-3 h-3" /> Edit
                        </button>
                        <button
                          onClick={() => setDeleting({ slug: p.slug, title: p.title })}
                          data-testid={`admin-post-delete-${p.slug}`}
                          className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#7A7068] hover:text-red-400 inline-flex items-center gap-1 px-2 py-1"
                          aria-label={`Delete ${p.title}`}
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <EditPostModal
        slug={editingSlug}
        onClose={() => setEditingSlug(null)}
        onSaved={() => { setEditingSlug(null); refresh(); }}
      />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent className="bg-[#1A1715] border-[#F5F2EB]/15 text-[#F5F2EB]" data-testid="admin-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Delete this post?</AlertDialogTitle>
            <AlertDialogDescription className="font-mono text-xs text-[#A39A8F] leading-relaxed">
              You're about to permanently delete <span className="text-[#F5F2EB]">{deleting?.title}</span>. This can't be undone. Live URLs will start 404-ing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="admin-delete-cancel"
              className="bg-transparent border-[#F5F2EB]/15 text-[#A39A8F] hover:bg-[#0F0D0C] hover:text-[#F5F2EB] font-mono text-xs uppercase tracking-[0.25em]"
            >Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-delete-confirm-btn"
              className="bg-red-600 hover:bg-red-700 text-white font-mono text-xs uppercase tracking-[0.25em]"
              onClick={async () => {
                const slug = deleting.slug;
                setDeleting(null);
                try {
                  await api.delete(`/admin/blog/posts/${slug}`);
                  toast.success("Post deleted.");
                  refresh();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Couldn't delete.");
                }
              }}
            >Delete forever</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Footer />
    </div>
  );
}

function EditPostModal({ slug, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [post, setPost] = useState(null);

  useEffect(() => {
    if (!slug) { setPost(null); return; }
    setLoading(true);
    api.get(`/admin/blog/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Couldn't load post."))
      .finally(() => setLoading(false));
  }, [slug]);

  if (!slug) return null;

  const setField = (k, v) => setPost((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const tagsArr = Array.isArray(post.tags) ? post.tags : (post.tags || "").split(",").map((s) => s.trim()).filter(Boolean);
      const payload = {
        title: post.title,
        content_html: post.content_html,
        excerpt: post.excerpt,
        cover_image: post.cover_image || null,
        tags: tagsArr,
        author: post.author,
        status: post.status,
        meta_description: post.meta_description,
      };
      await api.put(`/admin/blog/posts/${slug}`, payload);
      toast.success("Saved.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!slug} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="admin-edit-modal"
        className="bg-[#1A1715] border-[#F5F2EB]/15 text-[#F5F2EB] max-w-3xl max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Edit post</DialogTitle>
          <DialogDescription className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F]">
            {slug}
          </DialogDescription>
        </DialogHeader>

        {loading || !post ? (
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#7A7068] py-12 text-center">
            Loading…
          </div>
        ) : (
          <div className="space-y-5">
            <Field label="Title">
              <Input
                data-testid="admin-edit-title"
                value={post.title || ""}
                onChange={(e) => setField("title", e.target.value)}
                className="bg-[#0F0D0C] border-[#F5F2EB]/15"
              />
            </Field>

            <Field label="Excerpt">
              <Textarea
                data-testid="admin-edit-excerpt"
                value={post.excerpt || ""}
                onChange={(e) => setField("excerpt", e.target.value)}
                rows={2}
                className="bg-[#0F0D0C] border-[#F5F2EB]/15 font-mono text-sm"
              />
            </Field>

            <Field label="Content (HTML)">
              <Textarea
                data-testid="admin-edit-content"
                value={post.content_html || ""}
                onChange={(e) => setField("content_html", e.target.value)}
                rows={16}
                className="bg-[#0F0D0C] border-[#F5F2EB]/15 font-mono text-xs leading-relaxed"
                spellCheck={false}
              />
              <div className="font-mono text-[10px] text-[#7A7068] mt-1">
                HTML is sanitized server-side (bleach allowlist). Reading time is auto-recomputed.
              </div>
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Author">
                <Input
                  data-testid="admin-edit-author"
                  value={post.author || ""}
                  onChange={(e) => setField("author", e.target.value)}
                  className="bg-[#0F0D0C] border-[#F5F2EB]/15"
                />
              </Field>
              <Field label="Cover image URL">
                <Input
                  data-testid="admin-edit-cover"
                  value={post.cover_image || ""}
                  onChange={(e) => setField("cover_image", e.target.value)}
                  placeholder="https://… or /…"
                  className="bg-[#0F0D0C] border-[#F5F2EB]/15"
                />
              </Field>
            </div>

            <Field label="Tags (comma-separated)">
              <Input
                data-testid="admin-edit-tags"
                value={Array.isArray(post.tags) ? post.tags.join(", ") : (post.tags || "")}
                onChange={(e) => setField("tags", e.target.value)}
                placeholder="whatsapp, ai, guide"
                className="bg-[#0F0D0C] border-[#F5F2EB]/15 font-mono text-xs"
              />
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Status">
                <div className="flex gap-2">
                  {["published", "draft"].map((s) => (
                    <button
                      key={s}
                      type="button"
                      data-testid={`admin-edit-status-${s}`}
                      onClick={() => setField("status", s)}
                      className={`font-mono text-[10px] uppercase tracking-[0.25em] px-4 py-2 border ${
                        post.status === s
                          ? "border-[#D95B38] text-[#D95B38]"
                          : "border-[#F5F2EB]/15 text-[#A39A8F] hover:text-[#F5F2EB]"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Meta description (SEO)">
                <Input
                  data-testid="admin-edit-meta"
                  value={post.meta_description || ""}
                  onChange={(e) => setField("meta_description", e.target.value)}
                  maxLength={200}
                  className="bg-[#0F0D0C] border-[#F5F2EB]/15 font-mono text-xs"
                />
              </Field>
            </div>
          </div>
        )}

        <DialogFooter className="mt-6">
          <Button
            type="button"
            variant="ghost"
            data-testid="admin-edit-cancel"
            onClick={onClose}
            className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#A39A8F] hover:text-[#F5F2EB] hover:bg-[#0F0D0C]"
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="admin-edit-save"
            onClick={save}
            disabled={loading || saving || !post}
            className="font-mono text-[11px] uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] hover:bg-[#c14d2e]"
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] mb-2">{label}</div>
      {children}
    </label>
  );
}
